import { computed, DestroyRef, inject } from '@angular/core';

import {
  updateState,
  withDevtools,
  withDevToolsStub,
  withGlitchTracking,
} from '@angular-architects/ngrx-toolkit';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { Store } from '@ngrx/store';

import { ConfigService } from '../services/config/config.service';
import { EquipmentService } from '../services/equipment/equipment.service';
import { FarmSwitchService } from '../services/farm-switch/farm-switch.service';
import { FarmStore } from './farm.store';
import { NotificationStore } from './notification.store';
import { go } from './ngrx/actions/router.action';
import { SuccessNotification } from '../models/notifications/app-notification';
import {
  Equipment,
  EQUIPMENT_DECIMAL_FIELDS,
} from '../entities/equipment/equipment.entities';
import {
  EquipmentQueryParams,
  EquipmentFilters,
  EquipmentCreateRequest,
  EquipmentUpdateRequest,
  EquipmentStatistics,
} from '../entities/equipment/equipment-api.types';
import { MaintenanceRecord } from '../entities/equipment/equipment-maintenance-record-item.entities';
import { MaintenanceSchedule } from '../entities/equipment/equipment-maintenance-schedule.entities';
import { LoadingMap } from './generic-entity.store';
import { withCondition } from './utils/with-condition';
import {
  deepMerge,
  parseDecimalStrings,
  upsertMany,
} from '../helpers/data.helpers';

interface EquipmentState {
  equipment: Equipment[];
  selectedEquipment: Equipment | null;
  loading: boolean;
  loadingMap: LoadingMap;
  error: string | null;
  totalEquipment: number;
  currentPage: number;
  pageSize: number;
  hasMoreEquipment: boolean;
  searchQuery: string;
  filters: EquipmentFilters;
  equipmentStatistics: EquipmentStatistics | null;
  maintenanceRecords: MaintenanceRecord[];
  maintenanceSchedules: MaintenanceSchedule[];
  loadingMaintenanceRecords: boolean;
  loadingMaintenanceSchedules: boolean;
}

const initialState: EquipmentState = {
  equipment: [],
  selectedEquipment: null,
  loading: false,
  loadingMap: {
    loading: false,
    loaded: false,
    error: null,
    pagination: {
      skip: 0,
      limit: 100,
      total: 0,
      loadedCount: 0,
      currentPage: 0,
      totalPages: 0,
      hasMore: false,
      loadedPages: new Set<number>(),
    },
    statistics: {
      loading: false,
      loaded: false,
      error: null,
    },
    search: {
      loading: false,
      error: null,
    },
  },
  error: null,
  totalEquipment: 0,
  currentPage: 0,
  pageSize: 100,
  hasMoreEquipment: false,
  searchQuery: '',
  filters: {},
  equipmentStatistics: null,
  maintenanceRecords: [],
  maintenanceSchedules: [],
  loadingMaintenanceRecords: false,
  loadingMaintenanceSchedules: false,
};

export const EquipmentStore = signalStore(
  withState(initialState),
  withProps(() => ({
    configService: inject(ConfigService),
    equipmentService: inject(EquipmentService),
    farmStore: inject(FarmStore),
    notificationStore: inject(NotificationStore),
    ngrxStore: inject(Store),
  })),
  withComputed(
    ({
      equipment,
      selectedEquipment,
      loadingMap,
      totalEquipment,
      hasMoreEquipment,
      equipmentStatistics,
      maintenanceRecords,
    }) => ({
      hasEquipment: computed(() => equipment().length > 0),
      equipmentCount: computed(() => equipment().length),
      totalEquipmentCount: computed(() => totalEquipment()),
      isLoading: computed(() => loadingMap().loading),
      isSearching: computed(() => loadingMap().search.loading),
      hasError: computed(() => loadingMap().error !== null),
      selectedEquipmentName: computed(() => selectedEquipment()?.name || null),
      hasMoreEquipmentAvailable: computed(() => hasMoreEquipment()),
      hasEquipmentStatistics: computed(() => equipmentStatistics() !== null),
      maintenanceRecordsCount: computed(() => maintenanceRecords().length),
    }),
  ),
  withCondition(
    ({ configService }) => !configService?.environment?.production,
    withDevtools('equipment', withGlitchTracking()),
    withDevToolsStub('equipment'),
  ),
  withMethods((store) => ({
    loadEquipment(
      params?: EquipmentQueryParams,
      filters?: EquipmentFilters,
      append = false,
    ): void {
      updateState(store, '[Equipment] Load Equipment Start', {
        loading: true,
        error: null,
        loadingMap: {
          ...store.loadingMap(),
          loading: true,
          error: null,
        },
      });

      const currentFarmId = store.farmStore.currentFarm()?.id;
      const farmFilters = {
        ...filters,
        ...(currentFarmId && { farm_id: currentFarmId }),
      };

      store.equipmentService.getEquipment(params, farmFilters).subscribe({
        next: (response) => {
          const parsedData = parseDecimalStrings(
            response.data,
            EQUIPMENT_DECIMAL_FIELDS,
          );
          const newEquipment = append
            ? upsertMany(store.equipment(), parsedData)
            : parsedData;
          updateState(store, '[Equipment] Load Equipment Success', {
            equipment: newEquipment,
            totalEquipment: response.total,
            currentPage: params?.skip
              ? Math.floor(
                (params.skip + response.data.length) / (params?.limit || 100),
              )
              : 0,
            pageSize: params?.limit || 100,
            hasMoreEquipment:
              (params?.skip || 0) + response.data.length < response.total,
            loading: false,
            error: null,
            filters: filters || {},
            searchQuery: '',
            loadingMap: {
              ...store.loadingMap(),
              loading: false,
              loaded: true,
              error: null,
            },
          });
        },
        error: (error) => {
          updateState(store, '[Equipment] Load Equipment Error', {
            loading: false,
            error: error.message || 'Failed to load equipment',
            loadingMap: {
              ...store.loadingMap(),
              loading: false,
              error: error.message || 'Failed to load equipment',
            },
          });
        },
      });
    },

    searchEquipment(
      query: string,
      params?: Omit<EquipmentQueryParams, 'q'>,
    ): void {
      const currentLoadingMap = store.loadingMap();
      updateState(store, '[Equipment] Search Equipment Start', {
        loadingMap: {
          ...currentLoadingMap,
          search: { loading: true, error: null },
        },
        searchQuery: query,
      });

      const currentFarmId = store.farmStore.currentFarm()?.id;
      const searchParams = { ...params, q: query };
      const farmFilters = {
        ...(currentFarmId && { farm_id: currentFarmId }),
      };

      store.equipmentService.getEquipment(searchParams, farmFilters).subscribe({
        next: (response) => {
          const parsedData = parseDecimalStrings(
            response.data,
            EQUIPMENT_DECIMAL_FIELDS,
          );
          const mergedEquipment = upsertMany(store.equipment(), parsedData);

          updateState(store, '[Equipment] Search Equipment Success', {
            equipment: mergedEquipment,
            loadingMap: {
              ...store.loadingMap(),
              search: { loading: false, error: null },
            },
            searchQuery: query,
          });
        },
        error: (error) => {
          const updatedLoadingMap = store.loadingMap();
          updateState(store, '[Equipment] Search Equipment Error', {
            loadingMap: {
              ...updatedLoadingMap,
              search: {
                loading: false,
                error: error.message || 'Failed to search equipment',
              },
            },
          });
        },
      });
    },

    loadEquipmentById(id: string): void {
      updateState(store, '[Equipment] Load Equipment By ID Start', {
        loading: true,
        error: null,
      });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Load Equipment By ID Error', {
          loading: false,
          error: 'Farm ID is required to get equipment',
        });
        return;
      }

      store.equipmentService.getEquipmentById(id, farmId).subscribe({
        next: (equipment) => {
          const parsedEquipment = parseDecimalStrings(
            equipment,
            EQUIPMENT_DECIMAL_FIELDS,
          );
          try {
            const existingIndex = store
              .equipment()
              .findIndex((e) => e.id === parsedEquipment.id);
            const newEquipment = [...store.equipment()];
            if (existingIndex >= 0) {
              newEquipment[existingIndex] = parsedEquipment;
            } else {
              newEquipment.push(parsedEquipment);
            }

            updateState(store, '[Equipment] Load Equipment By ID Success', {
              equipment: newEquipment,
              loading: false,
              error: null,
            });
          } catch (_err) {
            updateState(store, '[Equipment] Load Equipment By ID Error', {
              loading: false,
              error: 'Failed to process load response',
            });
          }
        },
        error: (error) => {
          updateState(store, '[Equipment] Load Equipment By ID Error', {
            loading: false,
            error: error.message || 'Failed to load equipment',
          });
        },
      });
    },

    createEquipment(equipmentData: EquipmentCreateRequest): void {
      updateState(store, '[Equipment] Create Equipment Start', {
        loading: true,
        error: null,
      });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Create Equipment Error', {
          loading: false,
          error: 'Farm ID is required to create equipment',
        });
        return;
      }

      store.equipmentService.createEquipment(equipmentData, farmId).subscribe({
        next: (response) => {
          const newEquipment: Equipment = {
            ...equipmentData,
            id: response.id,
            farm_id: farmId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          updateState(store, '[Equipment] Create Equipment Success', {
            equipment: [...store.equipment(), newEquipment],
            totalEquipment: store.totalEquipment() + 1,
            loading: false,
            error: null,
          });

          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'notifications.success',
              detail: 'equipment.notifications.created',
            }),
          );

          store.ngrxStore.dispatch(go({ path: ['/app/equipment/tiles'] }));
        },
        error: (error) => {
          updateState(store, '[Equipment] Create Equipment Error', {
            loading: false,
            error: error.message || 'Failed to create equipment',
          });
        },
      });
    },

    updateEquipment(id: string, updates: EquipmentUpdateRequest): void {
      updateState(store, '[Equipment] Update Equipment Start', {
        loading: true,
        error: null,
      });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Update Equipment Error', {
          loading: false,
          error: 'Farm ID is required to update equipment',
        });
        return;
      }

      store.equipmentService.patchEquipment(id, updates, farmId).subscribe({
        next: () => {
          const currentEquipment = store.equipment().find((e) => e?.id === id);
          if (!currentEquipment) {
            updateState(store, '[Equipment] Update Equipment Error', {
              loading: false,
              error: 'Equipment not found in store after update',
            });
            return;
          }

          const updatedEquipment: Equipment = {
            ...deepMerge(currentEquipment, updates as Partial<Equipment>),
            updated_at: new Date().toISOString(),
          };

          const newEquipment = store
            .equipment()
            .map((equipment) =>
              equipment?.id === id ? updatedEquipment : equipment,
            );

          updateState(store, '[Equipment] Update Equipment Success', {
            equipment: newEquipment,
            loading: false,
            error: null,
          });

          if (store.selectedEquipment()?.id === id) {
            updateState(store, '[Equipment] Update Selected Equipment', {
              selectedEquipment: updatedEquipment,
            });
          }

          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'notifications.success',
              detail: 'equipment.notifications.updated',
            }),
          );
        },
        error: (error) => {
          updateState(store, '[Equipment] Update Equipment Error', {
            loading: false,
            error: error.message || 'Failed to update equipment',
          });
        },
      });
    },

    patchEquipment(id: string, updates: Partial<EquipmentUpdateRequest>): void {
      updateState(store, '[Equipment] Patch Equipment Start', {
        loading: true,
        error: null,
      });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Patch Equipment Error', {
          loading: false,
          error: 'Farm ID is required to patch equipment',
        });
        return;
      }

      store.equipmentService.patchEquipment(id, updates, farmId).subscribe({
        next: () => {
          const currentEquipment = store.equipment().find((e) => e?.id === id);
          if (!currentEquipment) {
            updateState(store, '[Equipment] Patch Equipment Error', {
              loading: false,
              error: 'Equipment not found in store after patch',
            });
            return;
          }

          const updatedEquipment: Equipment = {
            ...deepMerge(currentEquipment, updates as Partial<Equipment>),
            updated_at: new Date().toISOString(),
          };

          const newEquipment = store
            .equipment()
            .map((equipment) =>
              equipment?.id === id ? updatedEquipment : equipment,
            );

          updateState(store, '[Equipment] Patch Equipment Success', {
            equipment: newEquipment,
            loading: false,
            error: null,
          });

          if (store.selectedEquipment()?.id === id) {
            updateState(store, '[Equipment] Update Selected Equipment', {
              selectedEquipment: updatedEquipment,
            });
          }

          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'notifications.success',
              detail: 'equipment.notifications.updated',
            }),
          );
        },
        error: (error) => {
          updateState(store, '[Equipment] Patch Equipment Error', {
            loading: false,
            error: error.message || 'Failed to patch equipment',
          });
        },
      });
    },

    deleteEquipment(id: string): void {
      updateState(store, '[Equipment] Delete Equipment Start', {
        loading: true,
        error: null,
      });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Delete Equipment Error', {
          loading: false,
          error: 'Farm ID is required to delete equipment',
        });
        return;
      }

      store.equipmentService.deleteEquipment(id, farmId).subscribe({
        next: () => {
          try {
            const newEquipment = store
              .equipment()
              .filter((equipment) => equipment.id !== id);
            updateState(store, '[Equipment] Delete Equipment Success', {
              equipment: newEquipment,
              totalEquipment: Math.max(0, store.totalEquipment() - 1),
              loading: false,
              error: null,
            });

            if (store.selectedEquipment()?.id === id) {
              updateState(store, '[Equipment] Clear Selected Equipment', {
                selectedEquipment: null,
              });
            }

            store.notificationStore.addNotification(
              new SuccessNotification({
                summary: 'Success',
                detail: 'Equipment deleted successfully',
              }),
            );
          } catch (_err) {
            updateState(store, '[Equipment] Delete Equipment Error', {
              loading: false,
              error: 'Failed to process delete response',
            });
          }
        },
        error: (error) => {
          updateState(store, '[Equipment] Delete Equipment Error', {
            loading: false,
            error: error.message || 'Failed to delete equipment',
          });
        },
      });
    },

    loadEquipmentStatistics(farmId: string): void {
      updateState(store, '[Equipment] Load Equipment Statistics Start', {
        loading: true,
        error: null,
        loadingMap: {
          ...store.loadingMap(),
          statistics: {
            loading: true,
            loaded: false,
            error: null,
          },
        },
      });

      store.equipmentService.getEquipmentStatistics(farmId).subscribe({
        next: (statistics) => {
          updateState(store, '[Equipment] Load Equipment Statistics Success', {
            equipmentStatistics: statistics,
            loading: false,
            error: null,
            loadingMap: {
              ...store.loadingMap(),
              statistics: {
                loading: false,
                loaded: true,
                error: null,
              },
            },
          });
        },
        error: (error) => {
          updateState(store, '[Equipment] Load Equipment Statistics Error', {
            loading: false,
            error: error.message || 'Failed to load equipment statistics',
            loadingMap: {
              ...store.loadingMap(),
              statistics: {
                loading: false,
                loaded: false,
                error: error.message || 'Failed to load equipment statistics',
              },
            },
          });
        },
      });
    },

    setEquipmentData(equipment: Equipment[]): void {
      updateState(store, '[Equipment] Set Equipment Data', {
        equipment,
        loading: false,
        error: null,
      });
    },

    setLoading(loading: boolean): void {
      updateState(store, '[Equipment] Set Loading', {
        loading,
        loadingMap: {
          ...store.loadingMap(),
          loading
        }
      });
    },

    selectEquipment(equipment: Equipment | null): void {
      updateState(store, '[Equipment] Select Equipment', {
        selectedEquipment: equipment,
      });
    },

    selectEquipmentById(equipmentId: string): void {
      const equipment =
        store.equipment().find((e) => e.id === equipmentId) || null;
      this.selectEquipment(equipment);
    },

    loadNextPage(): void {
      if (!store.hasMoreEquipment()) return;

      const skip = store.currentPage() * store.pageSize();
      const params: EquipmentQueryParams = {
        skip,
        limit: store.pageSize(),
      };

      this.loadEquipment(params, store.filters(), true);
    },

    setFilters(filters: EquipmentFilters): void {
      updateState(store, '[Equipment] Set Filters', { filters });
      this.loadEquipment(undefined, filters);
    },

    clearFilters(): void {
      updateState(store, '[Equipment] Clear Filters', {
        filters: {},
        searchQuery: '',
      });
      this.loadEquipment();
    },

    refreshEquipment(): void {
      this.loadEquipment();
    },

    clearError(): void {
      updateState(store, '[Equipment] Clear Error', { error: null });
    },

    reset(): void {
      updateState(store, '[Equipment] Reset', initialState);
    },

    initializeEquipment(): void {
      // Load equipment for the current farm
      this.loadEquipment();
    },

    getEquipmentById(id: string): Equipment | undefined {
      const equipment = store.equipment();
      if (!equipment) {
        return undefined;
      }
      return equipment.find((equipment) => equipment.id === id);
    },

    hasAccessToEquipment(equipmentId: string): boolean {
      const equipment = store.equipment();
      if (!equipment) {
        return false;
      }
      return equipment.some((equipment) => equipment.id === equipmentId);
    },

    shouldLoadList(): boolean {
      const map = store.loadingMap();
      return !map.loading && !map.loaded;
    },

    shouldLoadById(id: string): boolean {
      const map = store.loadingMap();
      const entityExists = store.equipment().some((e) => e.id === id);
      return !map.loading && !entityExists;
    },

    shouldLoadStatistics(): boolean {
      const map = store.loadingMap();
      return !map.statistics.loading && !map.statistics.loaded;
    },

    loadMaintenanceRecords(equipmentId: string): void {
      updateState(store, '[Equipment] Load Maintenance Records Start', {
        loadingMaintenanceRecords: true,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Load Maintenance Records Error', {
          loadingMaintenanceRecords: false,
          error: 'No farm selected',
        });
        return;
      }
      store.equipmentService
        .getEquipmentMaintenanceRecords(equipmentId, farmId)
        .subscribe({
          next: (records) => {
            updateState(store, '[Equipment] Load Maintenance Records Success', {
              maintenanceRecords: records,
              loadingMaintenanceRecords: false,
              error: null,
            });
          },
          error: (error) => {
            updateState(store, '[Equipment] Load Maintenance Records Error', {
              loadingMaintenanceRecords: false,
              error: error.message || 'Failed to load maintenance records',
            });
          },
        });
    },

    loadMaintenanceSchedules(equipmentId: string): void {
      updateState(store, '[Equipment] Load Maintenance Schedules Start', {
        loadingMaintenanceSchedules: true,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Equipment] Load Maintenance Schedules Error', {
          loadingMaintenanceSchedules: false,
          error: 'No farm selected',
        });
        return;
      }
      store.equipmentService
        .getEquipmentMaintenanceSchedules(equipmentId, farmId)
        .subscribe({
          next: (schedules) => {
            updateState(
              store,
              '[Equipment] Load Maintenance Schedules Success',
              {
                maintenanceSchedules: schedules,
                loadingMaintenanceSchedules: false,
                error: null,
              },
            );
          },
          error: (error) => {
            updateState(store, '[Equipment] Load Maintenance Schedules Error', {
              loadingMaintenanceSchedules: false,
              error: error.message || 'Failed to load maintenance schedules',
            });
          },
        });
    },

    loadAllMaintenanceData(equipmentId: string): void {
      this.loadMaintenanceRecords(equipmentId);
      this.loadMaintenanceSchedules(equipmentId);
    },
  })),
  withHooks({
    onInit(store) {
      const destroyRef = inject(DestroyRef);
      const farmSwitchService = inject(FarmSwitchService);

      farmSwitchService.onFarmSwitch$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => {
          store.reset();
        });
    },
  }),
);
